import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Title from 'components/UI/Title';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import Skeleton from 'components/UI/Skeleton';
import Button from 'components/UI/Button';
import Col from 'components/UI/Layout/Col';
import Row from 'components/UI/Layout/Row';

function CollapsibleCard({
    children, title, icon, className, boxClassName, customBg, customButtonBg, before, titleMargin, boxMargin, titleType, pingState, defaultState, storeId, subtitle, subtitleMargin, subtitlePadding, margin, padding,
})
{
    let currentState = {};
    try
    {
        currentState = JSON.parse(window?.localStorage?.getItem('cardState')) ?? {};
    }
    catch (e)
    {
        window?.localStorage?.setItem('cardState', JSON.stringify(currentState));
    }

    const [loaded, setLoaded] = useState(currentState?.[storeId] ?? defaultState);
    const [state, setState] = useState(currentState?.[storeId] ?? defaultState);

    useEffect(() =>
    {
        if (storeId)
        {
            currentState[storeId] = state;
            window?.localStorage?.setItem('cardState', JSON.stringify(currentState));
        }

        setLoaded(loaded || state);
    }, [state]);

    const loadedFallback = useMemo(() => React.Children.map(children, (child) =>
    {
        if (React.isValidElement(child))
        {
            return React.cloneElement(child, {
                fallback: <Skeleton className="tw-ml-5 tw-mt-3" type="list" />,
            });
        }

        return child;
    }), [children]);

    return (
        <Col className={`${customBg || 'tw-bg-ivory-focus dark:tw-bg-neutral-900'} tw-rounded ${padding || 'tw-p-3'} ${margin || 'tw-mt-6'} ${className}`}>
            <Row className="tw-justify-between">
                {title && <Title subtitle={subtitle} before={state && before} subtitlePadding={subtitlePadding ?? 'tw-pt-0 tw-pl-5'} subtitleMargin={subtitleMargin ?? 'tw-mt-0 tw-ml-3 tw-mb-3'} type={titleType || 'primary'} padding="tw-pt-0" margin={titleMargin || (`${state ? 'tw-mt-5 tw-mb-0' : 'tw-my-5'} tw-ml-3 tw-pl-5`)} title={title} icon={icon} />}
                <Button
                    displayLabel={state ? 'Close Section' : 'Open Section'}
                    customBg={customButtonBg || 'hover:tw-bg-primary-default dark:tw-text-white hover:tw-text-white tw-text-primary-default'}
                    className="tw-self-center tw-relative tw-flex tw-items-center"
                    type="custom"
                    margin="tw-mt-0"
                    iconMargin="tw-mr-0"
                    rounded="tw-rounded-full"
                    onClick={() =>
                    {
                        setState(!state);
                    }}
                    icon={!state ? faChevronDown : faChevronUp}
                >
                    {pingState && <span className="tw-animate-ping tw-absolute tw-inline-flex tw-h-[50%] tw-w-[50%] tw-left-1/4 tw-top-1/4 tw-rounded-full tw-bg-primary-hover tw-opacity-75"></span>}
                </Button>
            </Row>

            <Row className={state ? `max-sm:tw-min-h-[100px] tw-min-h-[50px] ${boxMargin || 'tw-pl-5 tw-mt-3 tw-pr-5 tw-ml-0'} ${boxClassName}` : 'tw-hidden'}>
                {loaded && loadedFallback}
            </Row>

        </Col>
    );
}

CollapsibleCard.defaultProps = {
    before: true,
    pingState: false,
    defaultState: false,
    storeId: null,
    boxClassName: '',
    className: '',
};

CollapsibleCard.propTypes = {
    storeId: PropTypes.string,
    className: PropTypes.string,
    margin: PropTypes.string,
    padding: PropTypes.string,
    boxClassName: PropTypes.string,
    titleType: PropTypes.string,
    customBg: PropTypes.string,
    customButtonBg: PropTypes.string,
    children: PropTypes.node,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    icon: PropTypes.object,
    defaultState: PropTypes.bool,
    before: PropTypes.bool,
    pingState: PropTypes.bool,
    titleMargin: PropTypes.string,
    boxMargin: PropTypes.string,
    subtitleMargin: PropTypes.string,
    subtitlePadding: PropTypes.string,
};

export default CollapsibleCard;
